import React, {useEffect, useState} from 'react';
import AnimateHeight from 'react-animate-height';
import ChevronUpIcon from "@heroicons/react/solid/ChevronUpIcon";
import ChevronDownIcon from "@heroicons/react/solid/ChevronDownIcon";
import ClockIcon from "@heroicons/react/solid/ClockIcon";

const BusinessHoursAccordion = ({location}) => {
    const [status, setStatus] = useState('');
    const [statusColor, setStatusColor] = useState('text-black');
    const [todayHours, setTodayHours] = useState('');
    const [expanded, setExpanded] = useState(false);
    const [weeklyHours, setWeeklyHours] = useState([]);

    useEffect(() => {
        const now = new Date();
        const todayIndex = now.getDay(); // 0 = Sunday
        const today = (todayIndex + 6) % 7; // Adjust to make Monday = 0

        const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

        const formatTime = (timeStr) => {
            if (!timeStr) return '';
            const [h, m] = timeStr.split(':').map(Number);
            const date = new Date();
            date.setHours(h, m);
            const hr = date.getHours();
            const min = date.getMinutes().toString().padStart(2, '0');
            const suffix = hr >= 12 ? 'PM' : 'AM';
            return `${((hr + 11) % 12 + 1)}:${min} ${suffix}`;
        };

        const formatRange = (start, end) => {
            if (!start || !end) return 'Closed';
            return `${formatTime(start)} – ${formatTime(end)}`;
        };

        const getStatus = (startStr, endStr) => {
            if (!startStr || !endStr) return { label: 'Closed', color: 'text-red-600' };

            const [sh, sm] = startStr.split(':').map(Number);
            const [eh, em] = endStr.split(':').map(Number);
            const start = new Date();
            const end = new Date();
            start.setHours(sh, sm, 0, 0);
            end.setHours(eh, em, 0, 0);

            if (now < start || now > end) return { label: 'Closed', color: 'text-red-600' };
            const minsToClose = Math.floor((end - now) / 60000);
            return minsToClose <= 30
                ? { label: 'Closing Soon', color: 'text-yellow-500' }
                : { label: 'Open Now', color: 'text-green-600' };
        };

        let weekly = [];

        if (location.departmentWorkHours?.timetable) {
            // Use the new format
            weekly = location.departmentWorkHours.timetable.map((entry, index) => {
                const isToday = index === today;
                const hours = entry.status === 'closed'
                    ? 'Closed'
                    : `${entry.openFormatted} – ${entry.closedFormatted}`;
                return {
                    day: entry.dayShort,
                    hours,
                    today: isToday,
                    status: entry.status,
                    openTime: entry.open,
                    closeTime: entry.closed,
                };
            });
        } else {
            // Fallback to the original format
            const hoursMap = {
                0: location.sundayWorkHours,
                1: location.weekdayWorkHours,
                2: location.weekdayWorkHours,
                3: location.weekdayWorkHours,
                4: location.weekdayWorkHours,
                5: location.weekdayWorkHours,
                6: location.saturdayWorkHours,
            };

            weekly = days.map((day, i) => {
                const idx = (i + 1) % 7; // Adjust index to match hoursMap
                const range = hoursMap[idx];
                const isToday = i === today;
                const hours = range ? formatRange(...range.split('-')) : 'Closed';
                return {
                    day,
                    hours,
                    today: isToday,
                    status: range ? 'open' : 'closed',
                    openTime: range ? range.split('-')[0] : '',
                    closeTime: range ? range.split('-')[1] : '',
                };
            });
        }

        const todayEntry = weekly.find((entry) => entry.today);
        const statusObj = getStatus(todayEntry.openTime, todayEntry.closeTime);

        setStatus(statusObj.label);
        setStatusColor(statusObj.color);
        setTodayHours(todayEntry.hours);
        setWeeklyHours(weekly);
    }, [location]);

    return (
        <div className="w-full text-black">
            <div
                onClick={() => setExpanded((prev) => !prev)}
                className="flex items-start space-x-2"
            >
                <ClockIcon className={"h-5 mt-0.5"}/>
                <div className="flex flex-col text-left">
                    <div className={`font-medium flex items-center gap-1 ${statusColor}`}>
                        {status}
                        <span>
                            {expanded ? (
                                <ChevronUpIcon className="w-4 h-4 text-gray-500"/>
                            ) : (
                                <ChevronDownIcon className="w-4 h-4 text-gray-500"/>
                            )}
                        </span>
                    </div>
                    <AnimateHeight duration={200} height={expanded ? 0 : 'auto'}>
                        <div className="text-sm text-black/60 transition-opacity duration-200 ease-in">
                            {todayHours}
                        </div>
                    </AnimateHeight>
                </div>
            </div>

            <AnimateHeight duration={250} height={expanded ? 'auto' : 0}>
                <div className="mt-2 text-sm ml-6">
                    {weeklyHours.map(({day, hours, today}) => (
                        <div key={day}
                             className={today ? 'font-semibold text-black flex justify-between py-[1px]' : 'text-black/60 flex justify-between py-[1px]'}>
                            <span>{day}</span>
                            <span>{hours}</span>
                        </div>
                    ))}
                </div>
            </AnimateHeight>
        </div>
    );
};

export default BusinessHoursAccordion;